import { Logger, NOOP_LOGGER } from "bernie-logger";
import {
  ApiOperations,
  DxPropertySearchQuery,
  MapPropertySearchById,
  MapPropertySearchByLatLong,
  OperationResult,
  PropertySearchQuery,
  CustomerSearchHistoryQuery,
  CustomerPropertySearchByIdQuery,
  CampaignRecommendationsWithOffersQuery,
  CampaignByIdQuery,
  PropertyFiltersQuery,
  CustomerSearchHistoryAllQuery,
  MerchCouponsQuery,
} from "__generated__/api";
import * as generated from "__generated__/api/types";
import { BEX_API_FETCH_ERROR, NO_PROPERTIES_FOUND } from "src/config/systemEvents";
import { merge, SerializedData } from "bernie-core";
import { toJS } from "mobx";
import { BernieGraphqlClient, FetchPolicy } from "bernie-graphql";
import { BexApiNoPropertiesFoundError } from "services/errors";

interface BexApiHeaders {
  "Device-User-Agent-ID"?: string;
  "Trace-ID"?: string;
  cookie?: any;
  "x-page-id"?: string | null;
}

const clientId = "blossom-flex-ui,unknown,unknown";
export class BexApiService {
  private readonly logger: Logger;
  private readonly headers: BexApiHeaders;
  private readonly host: string;
  private apiOperations: ApiOperations;

  constructor(host = "", logger: Logger = NOOP_LOGGER, headers: BexApiHeaders) {
    this.headers = headers;
    this.host = host;
    this.logger = logger;
    this.buildApiOperations();
  }

  private buildApiOperations(): void {
    this.apiOperations = ApiOperations.withClient(createApolloClient(this.host, this.logger, this.headers));
  }

  public fetchMapHotelsForLatLong(
    variables: MapPropertySearchByLatLong.Variables
  ): Promise<OperationResult<generated.MapPropertySearchByLatLong.Query>> {
    return this.apiOperations.mapPropertySearchByLatLong(variables, FetchPolicy.CACHE_FIRST).catch((err) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch hotels for lat long", err);
      throw err;
    });
  }

  public fetchMapHotelsById(
    variables: MapPropertySearchById.Variables
  ): Promise<OperationResult<generated.MapPropertySearchById.Query>> {
    return this.apiOperations
      .mapPropertySearchById(variables, FetchPolicy.CACHE_FIRST)
      .then((result) => {
        if (!result.data.propertySearch.properties || !result.data.propertySearch.properties.length) {
          throw new BexApiNoPropertiesFoundError();
        }

        return result;
      })
      .catch((err) => {
        if (err instanceof BexApiNoPropertiesFoundError) {
          this.logger.logEvent(
            NO_PROPERTIES_FOUND,
            "No properties found when calling fetchMapHotelsById",
            JSON.stringify(variables)
          );
          throw err;
        }

        this.logger.logEvent(
          BEX_API_FETCH_ERROR,
          "Error occurred when calling fetchMapHotelsById",
          err,
          JSON.stringify(variables)
        );
        throw err;
      });
  }

  public fetchPropertySearchQuery(
    variables: PropertySearchQuery.Variables
  ): Promise<OperationResult<generated.PropertySearchQuery.Query>> {
    return this.apiOperations.propertySearchQuery(variables, FetchPolicy.CACHE_FIRST).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch propertySearch", err);
      throw err;
    });
  }

  public fetchDxPropertySearchQuery(
    variables: DxPropertySearchQuery.Variables
  ): Promise<OperationResult<generated.DxPropertySearchQuery.Query>> {
    return this.apiOperations.dxPropertySearchQuery(variables, FetchPolicy.CACHE_AND_NETWORK).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch propertySearch", err);
      throw err;
    });
  }

  public fetchCustomerSearchHistoryQuery(
    variables: CustomerSearchHistoryQuery.Variables
  ): Promise<OperationResult<generated.CustomerSearchHistoryQuery.Query>> {
    return this.apiOperations.customerSearchHistoryQuery(variables, FetchPolicy.CACHE_FIRST).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch customerSearchHistory", err);
      throw err;
    });
  }

  public fetchCustomerSearchAllHistoryQuery(
    variables: CustomerSearchHistoryAllQuery.Variables
  ): Promise<OperationResult<generated.CustomerSearchHistoryAllQuery.Query>> {
    return this.apiOperations.customerSearchHistoryAllQuery(variables, FetchPolicy.CACHE_FIRST).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch customerSearchHistory", err);
      throw err;
    });
  }

  public fetchCustomerPropertySearchByIdQuery(
    variables: CustomerPropertySearchByIdQuery.Variables
  ): Promise<OperationResult<generated.CustomerPropertySearchByIdQuery.Query>> {
    return this.apiOperations
      .customerPropertySearchByIdQuery(variables, FetchPolicy.CACHE_FIRST)
      .catch((err: Error) => {
        this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch customerSearchHistory", err);
        throw err;
      });
  }

  public fetchCampaignRecommendationsWithOffersQuery(
    variables: CampaignRecommendationsWithOffersQuery.Variables
  ): Promise<OperationResult<generated.CampaignRecommendationsWithOffersQuery.Query>> {
    return this.apiOperations
      .campaignRecommendationsWithOffersQuery(variables, FetchPolicy.NO_CACHE)
      .catch((err: Error) => {
        this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch campaignRecommendations", err);
        throw err;
      });
  }

  public fetchCampaignByIdQuery(
    variables: CampaignByIdQuery.Variables
  ): Promise<OperationResult<generated.CampaignByIdQuery.Query>> {
    return this.apiOperations.campaignByIdQuery(variables, FetchPolicy.NO_CACHE).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch deals offersquery", err);
      throw err;
    });
  }

  public fetchMerchCouponsQuery(
    variables: MerchCouponsQuery.Variables
  ): Promise<OperationResult<generated.MerchCouponsQuery.Query>> {
    return this.apiOperations.merchCouponsQuery(variables, FetchPolicy.NO_CACHE).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch merch coupons query", err);
      throw err;
    });
  }

  public fetchPropertyFilterQuery(
    variables: PropertyFiltersQuery.Variables
  ): Promise<OperationResult<generated.PropertyFiltersQuery.Query>> {
    return this.apiOperations.propertyFiltersQuery(variables, FetchPolicy.CACHE_FIRST).catch((err: Error) => {
      this.logger.logEvent(BEX_API_FETCH_ERROR, "failed to fetch propertyFiltersQuery", err);
      throw err;
    });
  }

  /**
   * Override the default `toJson` to exclude the Apollo client.
   * If we don't include this, we will get a JSON serialization error involving
   * circular JSON.
   *
   * Creates a basic JSON object from the store, excluding
   * any fields provided in `ignoreAdditionalKeys`.
   */
  public toJSON(ignoreAdditionalKeys: string[] | string = []): SerializedData {
    if (!Array.isArray(ignoreAdditionalKeys)) {
      ignoreAdditionalKeys = ignoreAdditionalKeys.constructor === String ? [ignoreAdditionalKeys] : [];
    }

    const regexIgnorePrivate = /^_.+/;
    const simpleObject: any = toJS(this);
    const ignoreKeys: string[] = Object.keys(simpleObject)
      .filter((key) => regexIgnorePrivate.test(key) || typeof simpleObject[key] === "function")
      .concat(ignoreAdditionalKeys, "logger", "api");

    return merge.withKeys(ignoreKeys).merge({}, simpleObject);
  }
}

const createApolloClient = (host: string, logger: Logger, headers: BexApiHeaders): BernieGraphqlClient =>
  new BernieGraphqlClient(
    {
      uri: `${host}/graphql`,
      headers: {
        ...headers,
        "Client-Info": clientId,
        "Content-Type": "application/json",
      },
    },
    logger
  );
